import { FC, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Loader from '@/components/Loader';
import { useGetUserLoginHistory } from '@/services/userService';
import type { LoginHistoryDay, LoginHistorySession, LoginHistoryView } from '@/types/user.types';
import { convertISODateToMMDDYYYY, convertISOToTime } from '@/lib/utils';

const VIEW_OPTIONS: { value: LoginHistoryView; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'DAILY_SUMMARY', label: 'Daily summary' },
];

const NO_LOGOUT = 'No logout recorded';

const formatDateTime = (iso: string) => `${convertISODateToMMDDYYYY(iso)} ${convertISOToTime(iso)}`;

// The API's day is already in the viewer's time zone; reformat the string so it can't shift.
const formatDay = (date: string) => {
  const [year, month, day] = date.split('-');
  return `${month}/${day}/${year}`;
};

const Row: FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="grid grid-cols-2 justify-between items-start">
    <div className="font-medium flex-shrink-0 text-black">{label}</div>
    <div className="text-end font-normal text-black">{value}</div>
  </div>
);

const SessionEntry: FC<{ session: LoginHistorySession }> = ({ session }) => (
  <li className="flex flex-col gap-1 border-b border-[#EDEDED] pb-3 last:border-b-0" data-testid="login-history-entry">
    <Row label="Logged in" value={formatDateTime(session.loginAt)} />
    <Row label="Logged out" value={session.logoutAt ? formatDateTime(session.logoutAt) : NO_LOGOUT} />
  </li>
);

const DayEntry: FC<{ day: LoginHistoryDay }> = ({ day }) => (
  <li className="flex flex-col gap-1 border-b border-[#EDEDED] pb-3 last:border-b-0" data-testid="login-history-entry">
    <div className="font-semibold text-black">{formatDay(day.date)}</div>
    <Row label="First login" value={day.firstLoginAt ? convertISOToTime(day.firstLoginAt) : '-'} />
    <Row label="Last logout" value={day.lastLogoutAt ? convertISOToTime(day.lastLogoutAt) : NO_LOGOUT} />
  </li>
);

/** Read-only Login Activity tab of the user side panel (DGR-1835). Rendered for admins only. */
export const UserLoginHistory: FC<{ userId: string }> = ({ userId }) => {
  const [view, setView] = useState<LoginHistoryView>('ALL');
  const { data, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } = useGetUserLoginHistory(
    userId,
    view
  );

  const pages = data?.pages ?? [];
  const sessions = pages.flatMap((page) => (page.view === 'ALL' ? page.sessions : []));
  const days = pages.flatMap((page) => (page.view === 'DAILY_SUMMARY' ? page.days : []));
  const isEmpty = view === 'ALL' ? sessions.length === 0 : days.length === 0;

  return (
    <section className="flex flex-col gap-4 w-full" aria-label="Login activity">
      <div className="flex flex-row items-center justify-end gap-4">
        <Select value={view} onValueChange={(value) => setView(value as LoginHistoryView)}>
          <SelectTrigger className="w-[160px]" aria-label="Login activity filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {VIEW_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Loader isLoading={isLoading} className="py-4">
        {isError ? (
          <p className="text-sm text-error">Couldn&apos;t load login activity</p>
        ) : isEmpty ? (
          <p className="text-sm text-placeholder">No login activity</p>
        ) : (
          <ul className="flex flex-col gap-3 text-sm">
            {view === 'ALL'
              ? sessions.map((session) => <SessionEntry key={session.id} session={session} />)
              : days.map((day) => <DayEntry key={day.date} day={day} />)}
          </ul>
        )}
      </Loader>

      {hasNextPage && !isError && (
        <Button
          type="button"
          variant="outline"
          className="self-center"
          isLoading={isFetchingNextPage}
          disabled={isFetchingNextPage}
          onClick={() => void fetchNextPage()}
        >
          Show more
        </Button>
      )}
    </section>
  );
};
